import type { EnemyKind } from "../generation/types";
import { SeededRandom } from "../core/SeededRandom";
import { PALETTE } from "./palettes";

export const MONSTER_FRAME_WIDTH = 64;
export const MONSTER_FRAME_HEIGHT = 96;
export const MONSTER_ATLAS_COLUMNS = 6;
export const MONSTER_ATLAS_ROWS = 6;

export type MonsterAnimation =
  | "idle"
  | "walk"
  | "attack"
  | "pain"
  | "resurrection"
  | "death"
  | "corpse";

export const MONSTER_ANIMATIONS: Readonly<
  Record<
    MonsterAnimation,
    Readonly<{ row: number; frames: number; frameSeconds: number; loop: boolean }>
  >
> = {
  idle: { row: 0, frames: 2, frameSeconds: 0.42, loop: true },
  walk: { row: 1, frames: 4, frameSeconds: 0.14, loop: true },
  attack: { row: 2, frames: 3, frameSeconds: 0.12, loop: false },
  pain: { row: 3, frames: 2, frameSeconds: 0.11, loop: false },
  resurrection: { row: 4, frames: 4, frameSeconds: 0.16, loop: false },
  death: { row: 5, frames: 6, frameSeconds: 0.13, loop: false },
  corpse: { row: 5, frames: 6, frameSeconds: 1, loop: false },
};

export class MonsterSpriteGenerator {
  private readonly cache = new Map<EnemyKind, HTMLCanvasElement>();

  public constructor(private readonly seed: string) {}

  public get(kind: EnemyKind): HTMLCanvasElement {
    const cached = this.cache.get(kind);
    if (cached) {
      return cached;
    }
    const canvas = document.createElement("canvas");
    canvas.width = MONSTER_FRAME_WIDTH * MONSTER_ATLAS_COLUMNS;
    canvas.height = MONSTER_FRAME_HEIGHT * MONSTER_ATLAS_ROWS;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas 2D context is unavailable");
    }
    context.imageSmoothingEnabled = false;
    const random = new SeededRandom(`${this.seed}:monster:${kind}`);
    for (let row = 0; row < MONSTER_ATLAS_ROWS; row += 1) {
      for (let frame = 0; frame < MONSTER_ATLAS_COLUMNS; frame += 1) {
        context.save();
        context.translate(frame * MONSTER_FRAME_WIDTH, row * MONSTER_FRAME_HEIGHT);
        this.drawMonster(context, kind, row, frame, random);
        context.restore();
      }
    }
    this.cache.set(kind, canvas);
    return canvas;
  }

  private drawMonster(
    context: CanvasRenderingContext2D,
    kind: EnemyKind,
    row: number,
    frame: number,
    random: SeededRandom,
  ): void {
    switch (kind) {
      case "rustSentinel":
        this.drawRustSentinel(context, row, frame);
        break;
      case "sporeCrawler":
        this.drawSporeCrawler(context, row, frame);
        break;
      case "guard":
        this.drawGuard(context, row, frame, random);
        break;
      case "stalker":
        this.drawStalker(context, row, frame, random);
        break;
      case "boneHound":
        this.drawBoneHound(context, row, frame, random);
        break;
      case "choirAcolyte":
        this.drawChoirAcolyte(context, row, frame, random);
        break;
      case "forgeWarden":
        this.drawForgeWarden(context, row, frame, random);
        break;
      case "abyssTyrant":
        this.drawAbyssTyrant(context, row, frame, random);
        break;
    }
  }

  private drawRustSentinel(context: CanvasRenderingContext2D, row: number, frame: number): void {
    context.save();
    if (row === 5) { context.translate(0, frame * 12); context.scale(1, 1 - frame * 0.13); }
    if (row === 3) context.translate(frame % 2 ? 2 : -2, 0);
    if (row === 4) context.translate(0, Math.max(0, 3-frame)*5);
    const stride = row === 1 ? Math.sin(frame * Math.PI/2)*4 : 0;
    context.fillStyle="#222924";
    context.fillRect(12+stride,64,15,24);context.fillRect(38-stride,64,15,24);
    context.fillStyle="#6f392a";
    context.fillRect(14,27,38,43);context.fillRect(3,31,13,31);context.fillRect(51,31,11,31);
    context.fillStyle="#a07146";
    context.fillRect(16,29,34,5);context.fillRect(6,33,9,4);context.fillRect(50,33,9,4);
    context.fillStyle="#404a43";
    context.fillRect(22,13,23,19);context.fillRect(20,38,28,21);
    context.fillStyle="#efb752";
    context.fillRect(25,20,17,4);
    context.fillStyle="#101915";
    for(let x=24;x<46;x+=5)context.fillRect(x,40,2,14);
    context.fillRect(2,51,21,10);context.fillRect(1,48,8,16);
    if(row===2 && frame===1){context.fillStyle="#ffd57b";context.fillRect(0,44,10,19);}
    context.restore();
  }

  private drawSporeCrawler(context: CanvasRenderingContext2D, row: number, frame: number): void {
    context.save();
    if(row===5){context.translate(0,frame*8);context.scale(1,1-frame*0.13);}
    if(row===4)context.translate(0,Math.max(0,3-frame)*4);
    const stride=row===1?Math.sin(frame*Math.PI/2)*5:0;
    context.lineWidth=4;context.strokeStyle="#65764a";
    for(let pair=0;pair<3;pair++)for(const side of [-1,1]){
      context.beginPath();context.moveTo(32+side*9,50+pair*9);
      context.lineTo(32+side*(22+pair),43+pair*10+stride*side);
      context.lineTo(32+side*29,65+pair*8-stride*side);context.stroke();
    }
    context.fillStyle=row===3?"#876347":"#344930";
    context.beginPath();context.ellipse(32,57,16,24,0,0,Math.PI*2);context.fill();
    context.fillStyle="#84944c";
    for(let i=0;i<5;i++){context.beginPath();context.arc(22+(i%3)*9,43+Math.floor(i/3)*14,5,0,Math.PI*2);context.fill();}
    context.fillStyle="#dce287";context.fillRect(23,67,5,4);context.fillRect(36,67,5,4);
    context.fillStyle="#d5c39a";
    const bite=row===2?frame*3:0;
    context.fillRect(22,74,4,7+bite);context.fillRect(38,74,4,7+bite);
    context.restore();
  }

  private drawGuard(
    context: CanvasRenderingContext2D,
    row: number,
    frame: number,
    random: SeededRandom,
  ): void {
    if (row === 5) {
      const collapse = Math.min(frame, 5);
      context.fillStyle = "rgba(0,0,0,.35)";
      context.fillRect(9, 82, 48, 7);
      context.fillStyle = PALETTE.rust;
      context.fillRect(24 - collapse * 3, 28 + collapse * 9, 18 + collapse * 5, 38 - collapse * 5);
      context.fillStyle = PALETTE.cyan;
      context.fillRect(28 + collapse, 32 + collapse * 8, Math.max(3, 10 - collapse), 3);
      return;
    }
    if (row === 4) {
      context.translate(0, Math.max(0, 3 - frame) * 6);
      context.fillStyle = "rgba(210,38,24,.2)";
      context.fillRect(10, 14, 44, 72);
    }
    const walkOffset = row === 1 ? (frame % 2 === 0 ? -3 : 3) : 0;
    const hurtOffset = row === 3 ? (frame === 0 ? -4 : 4) : 0;
    context.fillStyle = "rgba(0,0,0,.38)";
    context.fillRect(10, 84, 44, 6);
    context.fillStyle = PALETTE.darkRust;
    context.fillRect(19 + walkOffset, 63, 10, 23);
    context.fillRect(36 - walkOffset, 63, 10, 23);
    context.fillStyle = "#8e3f31";
    context.fillRect(16 + hurtOffset, 34, 32, 34);
    context.fillStyle = PALETTE.steel;
    context.fillRect(8 + hurtOffset, 34, 14, 31);
    context.fillRect(44 + hurtOffset, 39, 8, 25);
    context.fillStyle = "#261f1b";
    context.fillRect(11 + hurtOffset, 37, 8, 24);
    context.fillStyle = PALETTE.iron;
    context.beginPath();
    context.moveTo(19 + hurtOffset, 34);
    context.lineTo(24 + hurtOffset, 17);
    context.lineTo(41 + hurtOffset, 17);
    context.lineTo(47 + hurtOffset, 34);
    context.fill();
    context.fillStyle = PALETTE.bone;
    context.fillRect(25 + hurtOffset, 21, 15, 11);
    context.fillStyle = PALETTE.amber;
    context.fillRect(25 + hurtOffset, 25, 15, 4);
    context.fillStyle = "#171513";
    context.fillRect(22 + hurtOffset, 17, 3, 24);
    context.fillRect(41 + hurtOffset, 18, 3, 20);
    context.fillStyle = PALETTE.black;
    context.fillRect(22 + hurtOffset, 46, 23, 7);
    context.fillStyle = row === 2 && frame === 1 ? PALETTE.amber : PALETTE.paleSteel;
    context.fillRect(row === 2 ? 5 : 22, row === 2 ? 44 : 50, 37, 6);
    for (let index = 0; index < 4; index += 1) {
      context.fillStyle = random.chance(0.5) ? "rgba(255,255,255,.12)" : "rgba(0,0,0,.12)";
      context.fillRect(random.int(16, 48), random.int(35, 68), 2, 2);
    }
  }

  private drawStalker(
    context: CanvasRenderingContext2D,
    row: number,
    frame: number,
    random: SeededRandom,
  ): void {
    if (row === 5) {
      const collapse = Math.min(frame, 5);
      context.fillStyle = "rgba(0,0,0,.4)";
      context.fillRect(5, 84, 54, 7);
      context.fillStyle = PALETTE.ash;
      context.beginPath();
      context.ellipse(32, 54 + collapse * 6, 26, Math.max(5, 25 - collapse * 4), 0, 0, Math.PI * 2);
      context.fill();
      return;
    }
    if (row === 4) {
      context.translate(0, Math.max(0, 3 - frame) * 7);
      context.fillStyle = "rgba(222,45,22,.22)";
      context.fillRect(4, 12, 56, 76);
    }
    const stride = row === 1 ? (frame % 2 === 0 ? -3 : 3) : 0;
    const attackReach = row === 2 ? frame * 4 : 0;
    context.fillStyle = "rgba(0,0,0,.4)";
    context.fillRect(4, 84, 56, 7);
    context.fillStyle = PALETTE.violet;
    context.fillRect(8 - stride, 66, 14, 21);
    context.fillRect(42 + stride, 66, 14, 21);
    context.fillStyle = PALETTE.ash;
    context.beginPath();
    context.ellipse(32, 51, 27, 29, 0, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "#39353e";
    context.beginPath();
    context.moveTo(11, 40);
    context.lineTo(3, 12);
    context.lineTo(24, 32);
    context.moveTo(53, 40);
    context.lineTo(61, 12);
    context.lineTo(40, 32);
    context.fill();
    context.fillStyle = PALETTE.black;
    context.fillRect(26, 31, 12, 36);
    context.fillStyle = row === 3 ? PALETTE.red : "#ff6a28";
    context.fillRect(29, 35, 6, 25);
    context.fillStyle = "#ffc057";
    context.fillRect(30, 40, 4, 16);
    context.fillStyle = "#5d2f25";
    context.fillRect(6, 25, 18, 13);
    context.fillRect(40, 25, 18, 13);
    context.fillStyle = row === 2 && frame === 1 ? "#ff6b38" : PALETTE.violet;
    context.fillRect(3 - attackReach, 47, 12 + attackReach, 13);
    context.fillRect(49, 47, 12 + attackReach, 13);
    for (let index = 0; index < 5; index += 1) {
      context.fillStyle = random.chance(0.5) ? "rgba(220,210,230,.1)" : "rgba(20,10,24,.15)";
      context.fillRect(random.int(9, 54), random.int(31, 72), 3, 3);
    }
  }

  private drawBoneHound(
    context: CanvasRenderingContext2D,
    row: number,
    frame: number,
    random: SeededRandom,
  ): void {
    if (row === 5) {
      const collapse = Math.min(frame, 5);
      context.fillStyle = "rgba(0,0,0,.42)";
      context.fillRect(5, 83, 54, 7);
      context.fillStyle = "#9d8d72";
      context.fillRect(7 + collapse * 2, 59 + collapse * 4, 50 - collapse * 4, Math.max(5, 22 - collapse * 3));
      return;
    }
    if (row === 4) {
      context.translate(0, Math.max(0, 3 - frame) * 5);
      context.fillStyle = "rgba(216,48,22,.22)";
      context.fillRect(3, 38, 58, 50);
    }
    const stride = row === 1 ? (frame % 2 === 0 ? -4 : 4) : 0;
    const lunge = row === 2 ? frame * 4 : 0;
    context.fillStyle = "rgba(0,0,0,.42)";
    context.fillRect(3, 84, 58, 6);
    context.fillStyle = "#3a2923";
    context.fillRect(10 - stride, 70, 8, 17);
    context.fillRect(27 + stride, 71, 8, 16);
    context.fillRect(45 - stride, 69, 8, 18);
    context.fillStyle = "#82745f";
    context.beginPath();
    context.ellipse(31, 60, 26, 17, -0.08, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "#b8aa8d";
    for (let rib = 0; rib < 5; rib += 1) {
      context.fillRect(18 + rib * 6, 49 + Math.abs(2 - rib), 3, 23);
    }
    context.fillStyle = "#5c3b31";
    context.fillRect(4 - lunge, 45, 20 + lunge, 19);
    context.fillStyle = "#d7c7a4";
    context.beginPath();
    context.moveTo(5 - lunge, 46);
    context.lineTo(1 - lunge, 30);
    context.lineTo(13 - lunge, 43);
    context.moveTo(18, 45);
    context.lineTo(24, 31);
    context.lineTo(25, 50);
    context.fill();
    context.fillStyle = row === 3 ? PALETTE.red : "#e8a127";
    context.fillRect(6 - lunge, 49, 5, 4);
    context.fillStyle = "#e6d6b5";
    for (let tooth = 0; tooth < 4; tooth += 1) {
      context.fillRect(3 - lunge + tooth * 4, 61, 2, 5);
    }
    for (let speck = 0; speck < 4; speck += 1) {
      context.fillStyle = random.chance(0.5) ? "rgba(245,230,195,.18)" : "rgba(24,12,9,.18)";
      context.fillRect(random.int(12, 52), random.int(48, 73), 2, 2);
    }
  }

  private drawChoirAcolyte(
    context: CanvasRenderingContext2D,
    row: number,
    frame: number,
    random: SeededRandom,
  ): void {
    if (row === 5) {
      const collapse = Math.min(frame, 5);
      context.fillStyle = "rgba(0,0,0,.4)";
      context.fillRect(7, 84, 50, 7);
      context.fillStyle = "#34203f";
      context.beginPath();
      context.ellipse(32, 62 + collapse * 5, 23 + collapse * 3, Math.max(5, 25 - collapse * 4), 0, 0, Math.PI * 2);
      context.fill();
      return;
    }
    if (row === 4) {
      context.translate(0, Math.max(0, 3 - frame) * 7);
      context.fillStyle = "rgba(130,47,181,.25)";
      context.fillRect(8, 11, 48, 76);
    }
    const sway = row === 1 ? (frame % 2 === 0 ? -2 : 2) : 0;
    const cast = row === 2 ? Math.min(frame, 2) * 6 : 0;
    context.fillStyle = "rgba(0,0,0,.42)";
    context.fillRect(7, 84, 50, 7);
    context.fillStyle = "#24152c";
    context.beginPath();
    context.moveTo(19 + sway, 38);
    context.lineTo(9 + sway, 85);
    context.lineTo(55 + sway, 85);
    context.lineTo(45 + sway, 38);
    context.fill();
    context.fillStyle = "#53305e";
    context.fillRect(18 + sway, 39, 28, 34);
    context.fillStyle = "#1a111d";
    context.beginPath();
    context.moveTo(17 + sway, 39);
    context.lineTo(25 + sway, 14);
    context.lineTo(39 + sway, 14);
    context.lineTo(47 + sway, 39);
    context.fill();
    context.fillStyle = PALETTE.bone;
    context.fillRect(25 + sway, 24, 15, 12);
    context.fillStyle = row === 3 ? PALETTE.red : "#b850ef";
    context.fillRect(27 + sway, 28, 11, 3);
    context.fillStyle = "#6a3d76";
    context.fillRect(5 - cast, 45, 18 + cast, 8);
    context.fillRect(41, 45, 18 + cast, 8);
    context.fillStyle = row === 2 ? "#d26bff" : "#8d4da2";
    context.fillRect(1 - cast, 43, 7, 12);
    context.fillRect(56 + cast, 43, 7, 12);
    for (let rune = 0; rune < 5; rune += 1) {
      context.fillStyle = random.chance(0.5) ? "rgba(220,118,255,.25)" : "rgba(0,0,0,.2)";
      context.fillRect(random.int(20, 44), random.int(43, 76), 2, 3);
    }
  }

  private drawForgeWarden(
    context: CanvasRenderingContext2D,
    row: number,
    frame: number,
    random: SeededRandom,
  ): void {
    if (row === 5) {
      const collapse = Math.min(frame, 5);
      context.fillStyle = "rgba(0,0,0,.46)";
      context.fillRect(3, 85, 58, 7);
      context.fillStyle = "#712f1f";
      context.fillRect(5, 48 + collapse * 7, 54, Math.max(7, 36 - collapse * 5));
      context.fillStyle = "#ff6d20";
      context.fillRect(25, 59 + collapse * 5, 14, Math.max(2, 15 - collapse * 2));
      return;
    }
    if (row === 4) {
      context.translate(0, Math.max(0, 3 - frame) * 6);
      context.fillStyle = "rgba(255,58,18,.25)";
      context.fillRect(2, 10, 60, 80);
    }
    const stride = row === 1 ? (frame % 2 === 0 ? -2 : 2) : 0;
    const attackReach = row === 2 ? frame * 3 : 0;
    context.fillStyle = "rgba(0,0,0,.5)";
    context.fillRect(2, 85, 60, 7);
    context.fillStyle = "#241c19";
    context.fillRect(8 - stride, 67, 17, 20);
    context.fillRect(39 + stride, 67, 17, 20);
    context.fillStyle = "#4c2920";
    context.beginPath();
    context.ellipse(32, 50, 29, 31, 0, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "#8d3e2b";
    context.fillRect(4, 25, 21, 18);
    context.fillRect(39, 25, 21, 18);
    context.fillRect(13, 17, 11, 13);
    context.fillRect(40, 17, 11, 13);
    context.fillStyle = "#17110f";
    context.fillRect(23, 22, 18, 49);
    context.fillStyle = row === 3 ? PALETTE.red : "#ff5b16";
    context.fillRect(27, 28, 10, 37);
    context.fillStyle = "#ffd066";
    context.fillRect(30, 34, 4, 24);
    context.fillStyle = "#5b554c";
    context.fillRect(2 - attackReach, 46, 18 + attackReach, 17);
    context.fillRect(44, 46, 18 + attackReach, 17);
    context.fillStyle = PALETTE.iron;
    for (let plate = 0; plate < 5; plate += 1) {
      context.fillRect(18 + plate * 6, 9 + plate, 5, 9);
    }
    for (let index = 0; index < 8; index += 1) {
      context.fillStyle = random.chance(0.5) ? "rgba(255,142,52,.15)" : "rgba(0,0,0,.2)";
      context.fillRect(random.int(7, 56), random.int(20, 78), 2, 2);
    }
  }

  private drawAbyssTyrant(
    context: CanvasRenderingContext2D,
    row: number,
    frame: number,
    random: SeededRandom,
  ): void {
    if (row === 5) {
      const collapse = Math.min(frame, 5);
      context.fillStyle = "rgba(0,0,0,.55)";
      context.fillRect(1, 86, 62, 7);
      context.fillStyle = "#591c16";
      context.fillRect(3, 44 + collapse * 8, 58, Math.max(6, 42 - collapse * 6));
      context.fillStyle = "#ff3b12";
      context.fillRect(25, 54 + collapse * 7, 15, Math.max(2, 20 - collapse * 3));
      return;
    }
    if (row === 4) {
      context.translate(0, Math.max(0, 3 - frame) * 6);
      context.fillStyle = "rgba(255,40,12,.3)";
      context.fillRect(1, 5, 62, 87);
    }
    const stride = row === 1 ? (frame % 2 === 0 ? -2 : 2) : 0;
    const strike = row === 2 ? frame * 4 : 0;
    context.fillStyle = "rgba(0,0,0,.56)";
    context.fillRect(1, 86, 62, 7);
    context.fillStyle = "#24100f";
    context.fillRect(7 - stride, 67, 18, 21);
    context.fillRect(39 + stride, 67, 18, 21);
    context.fillStyle = "#6e2119";
    context.beginPath();
    context.ellipse(32, 49, 30, 34, 0, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "#aa3922";
    context.fillRect(4 - strike, 38, 21 + strike, 24);
    context.fillRect(39, 38, 21 + strike, 24);
    context.fillStyle = "#d0b27b";
    context.beginPath();
    context.moveTo(19, 26);
    context.lineTo(3, 2);
    context.lineTo(28, 20);
    context.moveTo(45, 26);
    context.lineTo(61, 2);
    context.lineTo(36, 20);
    context.fill();
    context.fillStyle = "#271212";
    context.fillRect(20, 17, 24, 48);
    context.fillStyle = row === 3 ? "#ffdf83" : "#ff4417";
    context.fillRect(24, 28, 6, 5);
    context.fillRect(35, 28, 6, 5);
    context.fillStyle = "#ff7b20";
    context.fillRect(27, 42, 11, 20);
    context.fillStyle = "#ffd072";
    context.fillRect(30, 46, 5, 12);
    for (let plate = 0; plate < 6; plate += 1) {
      context.fillStyle = plate % 2 === 0 ? "#481813" : "#8a2c1d";
      context.fillRect(14 + plate * 7, 8 + Math.abs(3 - plate), 6, 9);
    }
    for (let ember = 0; ember < 9; ember += 1) {
      context.fillStyle = random.chance(0.5) ? "rgba(255,99,22,.24)" : "rgba(0,0,0,.24)";
      context.fillRect(random.int(7, 57), random.int(20, 78), 2, 2);
    }
  }
}
